import clsx from 'clsx'
import RecommendedProducts from 'components/cart/RecommendedProducts'
import LoadingOverlay from 'components/common/LoadingOverlay'
import Button from 'components/forms/Button'
import MinusIcon from 'components/icons/MinusIcon'
import PlusIcon from 'components/icons/PlusIcon'
import useSlug from 'hooks/useSlug'
import formatMoney from 'lib/formatMoney'
import { inProduction } from 'lib/misc'
import {
  isAbsent,
  isEmptyObj,
  isNonEmptyArray,
  isPresent,
} from 'lib/utils'
import useCheckSiteStatus from 'hooks/useCheckSiteStatus'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  ICategory,
  IProduct,
  selectCategoryList,
  selectProductList,
  useAddCartItemMutation,
  useGetProductDetailQuery,
} from 'store/api'
import ProductImages from './ProductCard/ProducstImages'
import ProductInfo from './ProductCard/ProductInfo'
import ProductPath from './ProductCard/ProductPath'
import { IProductDetail } from './ProductCard/types'
import useIngredients from './ProductCard/useIngredients'
import useVariations from './ProductCard/useVariations'
import { ProductFacts, ServesLabel } from './ProductFacts'
import { buildProductGraphRuntime } from 'lib/productFactsAdapter'
import { buildProductGraph } from 'lib/jsonLd'

const ProductIng = dynamic(() => import('./ProductCard/ProductIng'))
const ProductVariations = dynamic(
  () => import('./ProductCard/ProductVariations')
)

type Props = {
  canonical: string
  categorySSR: ICategory
  productSSR: IProduct
  productDetailSSR: IProductDetail | null
}

const ProductView = ({
  canonical,
  productSSR,
  categorySSR,
  productDetailSSR,
}: Props) => {
  let { slug, queryLng } = useSlug()
  let _products = useSelector(selectProductList(queryLng))
  let _categoryList = useSelector(selectCategoryList(queryLng))

  let category = useMemo(() => {
    if (!isEmptyObj(categorySSR)) return categorySSR
    const foundCategory = isNonEmptyArray(_categoryList)
      ? _categoryList?.find((c) => c.slug === slug![0])
      : null

    return isPresent(foundCategory)
      ? foundCategory
      : !isEmptyObj(categorySSR)
      ? categorySSR
      : null
  }, [_categoryList, slug, categorySSR])

  let product = useMemo(() => {
    if (!isEmptyObj(productSSR)) return productSSR
    const productsByCategory = isPresent(category)
      ? _products[category.id]
      : []

    const foundProduct = isNonEmptyArray(productsByCategory)
      ? productsByCategory.find((p) => p.slug === slug![1])
      : null

    return isPresent(foundProduct)
      ? foundProduct
      : !isEmptyObj(productSSR)
      ? productSSR
      : null
  }, [category, _products, slug, productSSR])

  const { data: _productDetail } = useGetProductDetailQuery(
    { lng: 'ru', id: isAbsent(product) ? 0 : product!.id },
    { skip: isAbsent(product) }
  )

  const productDetail: IProductDetail | undefined = useMemo(
    () =>
      isPresent(productDetailSSR) ? productDetailSSR : _productDetail,
    [_productDetail, productDetailSSR]
  )

  const [quantity, setQuantity] = useState(1)

  const handleDecreaseQty = useCallback(() => {
    quantity > 1 && setQuantity((prev) => prev - 1)
  }, [quantity])

  const {
    variationId,
    onVariationChanged,
    variationPrice: price,
    variationWeight: weight,
    showVariations,
    nutritionValue,
  } = useVariations(
    productDetail,
    product?.type || 'simple',
    product?.id || -1,
    product?.price || 0,
    product?.weight || ''
  )

  const productRuntime = useMemo(() => buildProductGraphRuntime({
    product, category, canonical, productDetail, price, weight,
    nutrition: nutritionValue,
  }, buildProductGraph), [product, category, canonical, productDetail, price, weight, nutritionValue])

  const {
    selectedIngs,
    handleToggleIng,
    multiplicity,
    //  maxQty,
    minQty,
    ingTotalCount,
    ingTotalSum,
    ingredientsList,
  } = useIngredients(
    product?.options || {},
    product?.groupsingsProps || {},
    quantity
  )

  // const ingDescription = useMemo(() => {
  //   if (+minQty > 0 && minQty != maxQty && +maxQty > 0)
  //     return (
  //       <span
  //         className={clsx({
  //           'text-warning': ingTotalCount < +minQty,
  //         })}
  //       >
  //         Минимум {minQty} (макс. {maxQty}):
  //       </span>
  //     )
  //   if (+minQty > 0 && (minQty == maxQty || +maxQty == 0))
  //     return (
  //       <span
  //         className={clsx({
  //           'text-warning': ingTotalCount < +minQty,
  //         })}
  //       >
  //         Минимум {minQty}:
  //       </span>
  //     )
  //   return null
  // }, [minQty, maxQty, ingTotalCount])

  const totalPrice = useMemo(
    () => (isPresent(price) ? price + ingTotalSum : 0),
    [price, ingTotalSum]
  )

  const [addCartItem, { isLoading: addCartItemLoading }] =
    useAddCartItemMutation()

  const _addCartItem = useCallback(async () => {
    if (isPresent(product)) {
      const variation_id =
        product.type == 'simple' ? product.variation_id : variationId
      await addCartItem({
        id: product.id,
        quantity,
        lng: queryLng,
        price: price!,
        variation_id,
        type: product.type,
        ingredients:
          selectedIngs.length > 0
            ? selectedIngs.map(([ing, qty]) => [
                ing.ingredientId,
                ing.ingredientVariationId,
                qty,
              ])
            : [],
      })
      if (inProduction) {
        /*window.gtag!('event', 'add_to_cart', {
          items: [
            {
              id: product.id,
              name: product.name,
              price: price!,
              quantity: quantity,
              variant: variation_id,
            },
          ],
        })*/
        window.ym!(16650304, 'reachGoal', 'addToCartYa')
        window.dataLayer.push({ ecommerce: null })
        window.dataLayer.push({
          event: 'addToCart',
          ecommerce: {
            currencyCode: 'RUB',
            add: {
              products: [
                {
                  id: product.id,
                  name: product.name,
                  price: price!,
                  quantity: quantity,
                  variant: variation_id,
                },
              ],
            },
          },
        })
      }
    }
  }, [
    addCartItem,
    product,
    price,
    quantity,
    queryLng,
    selectedIngs,
    variationId,
  ])

  const maxQtyAchieved = useMemo(() => {
    return isPresent(product)
      ? quantity == product.options?.prod_max_quantity
      : false
  }, [product, quantity])

  const handleAddCartItem = useCallback(async () => {
    await _addCartItem()
    setQuantity(1)
  }, [_addCartItem])

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      isPresent(product) &&
      inProduction
    ) {
      /*window.gtag!('event', 'view_item', {
        items: [
          {
            id: product.id,
            name: product.name,
            price: product.price,
          },
        ],
      })*/
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        ecommerce: {
          currencyCode: 'RUB',
          detail: {
            products: [
              {
                id: product.id,
                name: product.name,
                price: product.price,
              },
            ],
          },
        },
      })
    }
  }, [])

  const { isAcceptingOrders, InfoBlock } = useCheckSiteStatus()

  const ingMultiplicityWarning = useMemo(
    () =>
      ingTotalCount !== 0 &&
      multiplicity !== 0 &&
      ingTotalCount % multiplicity !== 0,
    [ingTotalCount, multiplicity]
  )

  if (
    isAbsent(category) ||
    isAbsent(product) ||
    isAbsent(productDetail)
  )
    return (
      <LoadingOverlay
        className='h-5 w-5 mx-2.5 bg-accent'
        fixed
        open
      />
    )

  return (
    <>
      <Head>
        <title>{productRuntime.title}</title>
        <meta name='apple-mobile-web-app-title' content={productRuntime.title} />
        <meta property='og:site_name' content={productRuntime.title} />
        <meta
          name='description'
          content={productRuntime.description}
        />
        <meta
          name='keywords'
          content={product.meta.product_keywords}
        />
        <script key='product-facts' type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: productRuntime.jsonLd }} />
      </Head>

      <>
        <div
          itemScope
          itemType='https://schema.org/Product'
          className='flex flex-col lg:flex-row mb-10 lg:border-b lg:border-grey'
        >
          <div className='lg:w-[40%] pr-4 md:pr-6 lg:pr-0 custom-plx '>
            <ProductPath product={product} category={category} />
            <div className='lg:pb-14'>
              <ProductImages
                product={product}
                productDetail={productDetail}
                classNames='lg:mr-14'
                isPage
              />
            </div>
          </div>

          <div className='relative flex flex-col justify-between lg:w-[60%] pt-4 lg:pt-[84px] lg:border-l border-grey'>
            <div className='pr-4 md:pr-6 custom-plx lg:px-8'>
              <ProductInfo
                product={product}
                fullDesc={productDetail?.product?.full_desc}
                weight={weight}
                {...nutritionValue}
              />

              <ProductFacts facts={productRuntime.facts} />
              <ServesLabel label={productRuntime.label} />

              {showVariations && (
                <ProductVariations
                  variations={
                    productDetail.productsProps[product?.id]?.meta
                  }
                  tagLabels={productDetail.product.meta.tagsLabels}
                  onVariationChange={onVariationChanged}
                />
              )}

              {isPresent(ingredientsList) &&
                isNonEmptyArray(ingredientsList) && (
                  <div>
                    {/* <p className='text-sm font-bold leading-6'>
                      {ingDescription}
                    </p> */}
                    <div className='divide-y-[1px] lg:divide-y-0 my-3 pr-2 flex flex-col lg:pr-40 max-h-[250px] gap-y-2 overflow-y-auto'>
                      {ingredientsList.map((ing: any) => (
                        <ProductIng
                          key={ing.ingredientId}
                          ing={ing}
                          groupsingsProps={product!.groupsingsProps}
                          selectedIngs={selectedIngs}
                          onClick={handleToggleIng}
                        />
                      ))}
                    </div>
                  </div>
                )}
            </div>

            <div className='sticky bottom-0 lg:static bg-white flex flex-col md:flex-row justify-between lg:items-center gap-2 lg:gap-4 p-4 lg:py-5 lg:px-10 shadow-inner w-full lg:max-h-[90px]'>
              <div className='text-sm leading-5 text-dark text-light'>
                {/* {selectedIngs.length > 0 &&
                  `Ингредиенты x${ingTotalCount} (${formatMoney(
                    ingTotalSum
                  )})`} */}
                {ingTotalCount == 0 && +minQty > 0 && (
                  <div className='text-warning'>
                    Необходимо выбрать блюдо
                  </div>
                )}
                {ingMultiplicityWarning && (
                  <div className='text-warning'>
                    Количество должно быть кратно {multiplicity}
                  </div>
                )}
              </div>
              {isAcceptingOrders ? (
                <div className='lg:w-[60%] flex items-center justify-end gap-4 flex-shrink-0'>
                  <Button
                    aria-label='Уменьшить количество'
                    onClick={handleDecreaseQty}
                    disabled={quantity < 2 || addCartItemLoading}
                    className={clsx(
                      'rounded-lg bg-lightYellow w-10 h-10 p-3.5',
                      {
                        'hover:bg-primary': quantity > 1,
                      }
                    )}
                  >
                    <MinusIcon
                      className={clsx(
                        'w-full h-full mx-auto my-auto',
                        {
                          'text-bodyGrey': quantity === 1,
                          'text-dark': quantity > 1,
                        }
                      )}
                    />
                  </Button>

                  <div className='text-sm leading-6 tracking-tight text-dark'>
                    {quantity}
                  </div>

                  <Button
                    aria-label='Увеличить количество'
                    onClick={() => setQuantity((prev) => prev + 1)}
                    className={clsx(
                      'rounded-lg bg-lightYellow w-10 h-10 p-3.5',
                      {
                        'hover:bg-primary':
                          !addCartItemLoading && !maxQtyAchieved,
                      }
                    )}
                    disabled={addCartItemLoading || maxQtyAchieved}
                  >
                    <PlusIcon className='w-full h-full mx-auto my-auto' />
                  </Button>

                  <Button
                    className='rounded-lg max-w-[300px] min-w-max flex-grow h-10 px-4 btn-primary py-2 text-sm leading-5 font-semibold tracking-tight relative'
                    onClick={handleAddCartItem}
                    disabled={
                      addCartItemLoading ||
                      (+minQty > 0 && ingTotalCount < +minQty) ||
                      ingMultiplicityWarning
                    }
                    loading={addCartItemLoading}
                    loadingClasses='h-3 w-3 mx-1 bg-accent'
                  >
                    <div className='h-full flex justify-between items-center gap-4'>
                      <span>В корзину</span>
                      <span
                        itemProp='offers'
                        itemScope
                        itemType='http://schema.org/Offer'
                        className='text-sm'
                      >
                        <meta
                          itemProp='price'
                          content={product.price.toString()}
                        />
                        <meta
                          itemProp='priceCurrency'
                          content='RUB'
                        />
                        {formatMoney(totalPrice)}
                      </span>
                    </div>
                  </Button>
                </div>
              ) : (
                <InfoBlock className='text-left leading-tight text-sm' />
              )}
            </div>
          </div>
        </div>

        <div className='custom-pl'>
          <RecommendedProducts />
        </div>
      </>
    </>
  )
}

export default ProductView
